import { useContext } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import MarkdownDisplay from "../components/markdownDisplay.jsx";
import PageHero from "../components/pageHero.jsx";
import Pagination from "../components/pagination";
import SectionContent from "../components/sectionContent.jsx";
import { NewsContext } from "../context/NewsContext.jsx";
import { formatPublishDate } from "../dateTimeUtils.js";
import { useLocalePath } from "../hooks/useLocalePath";
import { absoluteUrl } from "../seoUtils";

const MAX_ITEMS_PER_PAGE = 6;
const MAX_PREVIEW_LENGTH = 255;

const NEWS_DESCRIPTION =
  "Nyheter från AS Maskiner — nya maskiner i lager, mässor, kundcase och uppdateringar från Sveriges ledande leverantör av maskiner för trä- och plastindustrin.";

const NewsCatalogue = () => {
  const { news } = useContext(NewsContext);
  const { locale } = useLocalePath();
  const [searchParams, setSearchParams] = useSearchParams();
  const newsUrl = absoluteUrl(locale, "/news");

  // Derived from the URL and context rather than mirrored into state via effects — effects
  // don't run during the server render, so an effect-populated list rendered as an
  // empty page for crawlers.
  const totalPageCount = Math.ceil(news.length / MAX_ITEMS_PER_PAGE) || 1;
  const currentPage = Math.min(
    Math.max(1, Number.parseInt(searchParams.get("page"), 10) || 1),
    totalPageCount
  );
  const paginatedItems = news.slice(
    (currentPage - 1) * MAX_ITEMS_PER_PAGE,
    currentPage * MAX_ITEMS_PER_PAGE
  );

  const handlePageChange = (pageNumber) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", pageNumber.toString());
    setSearchParams(newParams);

    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>AS Maskiner - Nyheter</title>
        <meta name="description" content={NEWS_DESCRIPTION} />
        <meta property="og:url" content={newsUrl} />
        <meta property="og:title" content="Nyheter - AS Maskiner" />
        <meta property="og:description" content={NEWS_DESCRIPTION} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            url: newsUrl,
            name: "Nyheter",
            description: NEWS_DESCRIPTION,
          })}
        </script>
      </Helmet>
      <PageHero
        titleKey="news.hero_title"
        titleFallback="Nyheter"
        subtitleKey="news.hero_subtitle"
        subtitleFallback=""
        backgroundKey="news.hero_background"
        alignment="bottom"
      />
      <section className="py-16 bg-white">
        <SectionContent>
          <div
            className={`grid gap-8 ${
              news.length === 1
                ? "grid-cols-1 max-w-xl mx-auto"
                : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
            }`}
          >
            {paginatedItems.map((article) => (
              <NewsPreview key={article.id} article={article} />
            ))}
          </div>
          <div className="flex flex-col items-center sm:basis-4/5">
            {news.length > MAX_ITEMS_PER_PAGE && (
              <Pagination
                currentPage={currentPage}
                totalPageCount={totalPageCount}
                onPageChange={handlePageChange}
                sx="mt-4 sm:mt-8"
              />
            )}
          </div>
        </SectionContent>
      </section>
    </div>
  );
};

/**@param article {NewsArticle}*/
const NewsPreview = ({ article }) => {
  const { localePath } = useLocalePath();
  const articleBodyPreview = () => {
    const body = article.body;
    if (body.length > MAX_PREVIEW_LENGTH) {
      return `${body.substring(0, MAX_PREVIEW_LENGTH)}...`;
    }
    return body;
  };
  return (
    <Link to={localePath(`/news/${article.id}`)}>
      <div className="bg-surface border border-border-default shadow-md overflow-hidden hover:shadow-lg transition-shadow flex flex-col h-full">
        <img src={article.imageUrl} alt={article.title} className="w-full h-48 object-cover" />
        <div className="p-4 flex flex-col flex-grow">
          <h3 className="text-xl font-semibold group-hover:text-primary transition-colors">
            {article.title}
          </h3>
          <div className="flex flex-row justify-between mb-1">
            <p className="text-text-muted text-sm mt-0">{formatPublishDate(article.createdAt)}</p>
            {!article.published && (
              <p className="text-inverse text-sm mt-2 p-1 bg-primary">ej publicerad</p>
            )}
          </div>
          <MarkdownDisplay data={articleBodyPreview()} />
        </div>
      </div>
    </Link>
  );
};

export default NewsCatalogue;
